// ============================================
// 進捗バー表示（パワー・シールド・ウェーブ進行）
// ============================================

import type p5 from 'p5';
import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config';
import {
    getSelectedShip,
    getPowerLevel,
    getPowerStock,
    getShieldActive,
    getShieldTimer,
    getShieldCharging,
    setShieldCharging,
    getWaveEnemiesDefeated
} from '../../store/index';
import {
    getEnemiesRequiredForWave,
    getLeftPanelCenterX
} from '../../utils/helpers';

// レイアウト定数
const PANEL_WIDTH = CONFIG.layout.panelWidth;
const BAR_WIDTH = PANEL_WIDTH - 20;  // パネル幅 - マージン
const BAR_HEIGHT = 6;

// 左パネル Y座標
const WAVE_PROGRESS_Y = 230;      // 敵カウンター(Y=215)の下
const POWER_LABEL_Y = 468;        // POWERラベル
const POWER_CENTER_Y = 520;       // リング中心

// パワーリング設定
const POWER_RING = {
    outer: {
        radius: 30,
        strokeWidth: 5,
        gap: 7,           // セグメント間ギャップ（度）
        activeColor: '#00f0ff',
        inactiveColor: 'rgba(0, 240, 255, 0.1)',
        maxColor: '#ffcc00',
        glowBlur: 4,
    },
    inner: {
        radius: 20,
        strokeWidth: 7,
        gap: 6,
        activeColor: '#ffcc00',
        inactiveColor: 'rgba(255, 204, 0, 0.1)',
        glowBlur: 3,
    },
    center: {
        fontSize: 20,
        fontFamily: "'Orbitron', monospace",
        fontWeight: 700,
        color: '#ffffff',
        maxColor: '#ffcc00',
        glowColor: 'rgba(255, 255, 255, 0.3)',
        maxGlowColor: 'rgba(255, 204, 0, 0.5)',
        glowBlur: 6,
    },
};

// 左パネル シールドインジケータ Y座標（最下部）
const SHIELD_LABEL_Y = 580;
const SHIELD_CENTER_Y = 630;

// シールドフェードアウト用状態
const FADE_DURATION = 300;  // フェード時間（ms）
let _lastShieldActive = false;
let _fadeStartTime = 0;
let _fadeColor: [number, number, number] = [255, 50, 80];  // フェード時の色（赤系）
let _fadePercentage = 0;

// シールドチャージアニメーション用状態
const CHARGE_DURATION = 400;  // チャージ時間（ms）- 早め
let _chargeStartTime = 0;
let _isCharging = false;
let _lastPercentage = 0;
let _chargeStartPercentage = 0;  // チャージ開始時の旧パーセンテージ

// パワーストック変化アニメーション用状態
const STOCK_FLASH_DURATION = 150;  // フラッシュ時間（ms）
let _lastPowerStock = 0;
let _stockFlashStartTime = 0;
let _stockFlashSegment = -1;  // フラッシュ対象セグメント（0-based）

// パワーレベルアップアニメーション用状態
const LEVEL_FLASH_DURATION = 250;   // 外側リングフラッシュ時間（ms）
const INNER_RESET_DURATION = 200;   // 内側リングリセットフェード時間（ms）
let _lastPowerLevel = 1;
let _levelFlashStartTime = 0;
let _levelFlashSegment = -1;        // フラッシュ対象の外側セグメント（0-based）
let _innerResetStartTime = 0;
let _innerResetPrevRequired = 0;    // リセット前の内側セグメント総数
let _innerResetPrevCount = 0;       // リセット前のアクティブ数

// MAXパルス用定数
const MAX_PULSE_SPEED = 2000;  // ms/サイクル

/**
 * 分割リングを描画（Canvas 2D API）
 */
function drawSegmentedRing(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    strokeWidth: number,
    totalSegments: number,
    activeCount: number,
    gapDegrees: number,
    activeColor: string,
    inactiveColor: string,
    glowBlur: number,
    alpha?: number
): void {
    const gapRad = gapDegrees * Math.PI / 180;
    const totalGap = gapRad * totalSegments;
    const availableArc = Math.PI * 2 - totalGap;
    const segmentArc = availableArc / totalSegments;
    const a = alpha ?? 1.0;

    let startAngle = -Math.PI / 2; // 12時位置から開始

    for (let i = 0; i < totalSegments; i++) {
        const isActive = i < activeCount;

        if (isActive) {
            ctx.shadowColor = activeColor;
            ctx.shadowBlur = glowBlur;
        } else {
            ctx.shadowBlur = 0;
        }

        ctx.beginPath();
        ctx.arc(cx, cy, radius, startAngle, startAngle + segmentArc);
        ctx.globalAlpha = isActive ? a : a * 0.5 + 0.5;
        ctx.strokeStyle = isActive ? activeColor : inactiveColor;
        ctx.lineWidth = strokeWidth;
        ctx.lineCap = 'butt';
        ctx.stroke();

        startAngle += segmentArc + gapRad;
    }

    ctx.globalAlpha = 1.0;
    ctx.shadowBlur = 0;
}

/**
 * 中央レベル数字を描画（Canvas 2D API）
 */
function drawCenterLevel(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    level: number,
    isMax: boolean,
    glowBlur?: number,
    alpha?: number
): void {
    const cfg = POWER_RING.center;
    const blur = glowBlur ?? cfg.glowBlur;

    ctx.font = `${cfg.fontWeight} ${cfg.fontSize}px ${cfg.fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.shadowColor = isMax ? cfg.maxGlowColor : cfg.glowColor;
    ctx.shadowBlur = blur;

    if (alpha !== undefined) {
        ctx.globalAlpha = alpha;
    }
    ctx.fillStyle = isMax ? cfg.maxColor : cfg.color;
    ctx.fillText(level.toString(), cx, cy);

    ctx.globalAlpha = 1.0;
    ctx.shadowBlur = 0;
}

/**
 * セグメントの開始角度を計算
 */
function getSegmentStartAngle(
    segmentIndex: number,
    totalSegments: number,
    gapDegrees: number
): number {
    const gapRad = gapDegrees * Math.PI / 180;
    const totalGap = gapRad * totalSegments;
    const availableArc = Math.PI * 2 - totalGap;
    const segmentArc = availableArc / totalSegments;
    return -Math.PI / 2 + segmentIndex * (segmentArc + gapRad);
}

/**
 * セグメントの弧長を計算
 */
function getSegmentArc(totalSegments: number, gapDegrees: number): number {
    const gapRad = gapDegrees * Math.PI / 180;
    const totalGap = gapRad * totalSegments;
    return (Math.PI * 2 - totalGap) / totalSegments;
}

/**
 * フラッシュオーバーレイを1セグメント描画
 */
function drawFlashOverlay(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    strokeWidth: number,
    segStartAngle: number,
    segArc: number,
    alpha: number,
    glowBlur: number
): void {
    ctx.shadowColor = `rgba(255, 255, 255, ${alpha * 0.8})`;
    ctx.shadowBlur = glowBlur * alpha;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, segStartAngle, segStartAngle + segArc);
    ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.9})`;
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'butt';
    ctx.stroke();
    ctx.shadowBlur = 0;
}

/**
 * パワーインジケータ表示（二重リング分割型）
 */
export function drawPowerLevelBar(): void {
    const p = getP5();
    const ctx = p.drawingContext as CanvasRenderingContext2D;
    const selectedShip = getSelectedShip();
    const powerLevel = getPowerLevel();
    const powerStock = getPowerStock();
    const shipCfg = CONFIG.ships[selectedShip];
    const leftCenterX = getLeftPanelCenterX();
    const isMax = powerLevel >= shipCfg.powerMaxLevel;
    const currentTime = p.millis();

    // --- 状態変化検出 ---

    // アイテム取得検出 → 内側リングフラッシュ
    if (powerStock > _lastPowerStock && powerStock > 0) {
        _stockFlashStartTime = currentTime;
        _stockFlashSegment = powerStock - 1;
    }
    _lastPowerStock = powerStock;

    // レベルアップ検出 → アイテム点灯 → 消灯 → 外側フラッシュ（3段階シーケンシャル）
    if (powerLevel > _lastPowerLevel && powerLevel > 1) {
        const oldRequired = shipCfg.powerRequired[_lastPowerLevel - 1] || 1;

        // Phase 1: 最後のアイテム取得フラッシュ（旧構成で全点灯）
        _stockFlashStartTime = currentTime;
        _stockFlashSegment = oldRequired - 1;

        // Phase 2: 内側リセットフェード（Phase 1完了後）
        _innerResetStartTime = currentTime + STOCK_FLASH_DURATION;
        _innerResetPrevRequired = oldRequired;
        _innerResetPrevCount = oldRequired;

        // Phase 3: 外側フラッシュ（Phase 2完了後）
        _levelFlashStartTime = currentTime + STOCK_FLASH_DURATION + INNER_RESET_DURATION;
        _levelFlashSegment = powerLevel - 1;
    }
    _lastPowerLevel = powerLevel;

    // --- MAXパルス計算 ---
    let outerGlow = POWER_RING.outer.glowBlur;
    let innerGlow = POWER_RING.inner.glowBlur;
    let centerGlow = POWER_RING.center.glowBlur;
    let maxPulseAlpha = 1.0;  // MAXパルス時の明るさ係数
    if (isMax) {
        const pulseT = (Math.sin(currentTime / MAX_PULSE_SPEED * Math.PI * 2) + 1) / 2;
        const glowMultiplier = 1.0 + pulseT * 2.0;
        outerGlow = POWER_RING.outer.glowBlur * glowMultiplier;
        innerGlow = POWER_RING.inner.glowBlur * glowMultiplier;
        centerGlow = POWER_RING.center.glowBlur * glowMultiplier;
        maxPulseAlpha = 0.6 + pulseT * 0.4;  // 0.6〜1.0で明るさ変動
    }

    // --- 描画開始 ---

    // POWERラベル（Share Tech Mono）
    p.textAlign(p.CENTER, p.TOP);
    p.textFont('Share Tech Mono');
    p.fill(255);
    p.textSize(11);
    p.text('POWER', leftCenterX, POWER_LABEL_Y);
    p.textFont('Orbitron');

    ctx.save();

    const outer = POWER_RING.outer;
    const inner = POWER_RING.inner;
    const cx = leftCenterX;
    const cy = POWER_CENTER_Y;

    // 外側リング（レベル表示）
    // 内側リセット中は旧レベル数で描画し、外側フラッシュ開始時に新レベルに切り替え
    const levelFlashElapsed = currentTime - _levelFlashStartTime;
    const isInLevelUpSequence = _levelFlashSegment >= 0 && levelFlashElapsed < 0;
    const outerActiveCount = isInLevelUpSequence ? powerLevel - 1 : powerLevel;

    drawSegmentedRing(
        ctx, cx, cy,
        outer.radius, outer.strokeWidth,
        shipCfg.powerMaxLevel, outerActiveCount, outer.gap,
        isMax ? outer.maxColor : outer.activeColor,
        outer.inactiveColor, outerGlow,
        isMax ? maxPulseAlpha : undefined
    );

    // 外側リング: レベルアップフラッシュ（内側リセット完了後に発火）
    if (_levelFlashSegment >= 0 && levelFlashElapsed >= 0 && levelFlashElapsed < LEVEL_FLASH_DURATION) {
        const flashAlpha = 1 - levelFlashElapsed / LEVEL_FLASH_DURATION;
        const segArc = getSegmentArc(shipCfg.powerMaxLevel, outer.gap);
        const segStart = getSegmentStartAngle(_levelFlashSegment, shipCfg.powerMaxLevel, outer.gap);
        drawFlashOverlay(ctx, cx, cy, outer.radius, outer.strokeWidth, segStart, segArc, flashAlpha, 15);
    }

    // レベルアップ中のPhase判定
    const innerResetElapsed = currentTime - _innerResetStartTime;
    const isLevelUpStockFlash = _levelFlashSegment >= 0 && currentTime < _innerResetStartTime;
    const isLevelUpReset = _innerResetPrevRequired > 0
        && innerResetElapsed >= 0 && innerResetElapsed < INNER_RESET_DURATION;

    // 内側リング: リセットフェードアウト（ゴースト）— Phase 2
    if (isLevelUpReset) {
        const resetAlpha = 1 - innerResetElapsed / INNER_RESET_DURATION;
        const oldSegArc = getSegmentArc(_innerResetPrevRequired, inner.gap);
        const oldGapRad = inner.gap * Math.PI / 180;
        let oldStartAngle = -Math.PI / 2;

        for (let i = 0; i < _innerResetPrevCount; i++) {
            ctx.shadowColor = `rgba(255, 204, 0, ${resetAlpha * 0.5})`;
            ctx.shadowBlur = inner.glowBlur * resetAlpha;
            ctx.beginPath();
            ctx.arc(cx, cy, inner.radius, oldStartAngle, oldStartAngle + oldSegArc);
            ctx.strokeStyle = `rgba(255, 204, 0, ${resetAlpha * 0.7})`;
            ctx.lineWidth = inner.strokeWidth;
            ctx.lineCap = 'butt';
            ctx.stroke();
            oldStartAngle += oldSegArc + oldGapRad;
        }
        ctx.shadowBlur = 0;
    }

    // 内側リング（アイテム進捗）
    if (isLevelUpStockFlash) {
        // Phase 1: 旧構成で全セグメント点灯 + 最後のセグメントにフラッシュ
        drawSegmentedRing(
            ctx, cx, cy,
            inner.radius, inner.strokeWidth,
            _innerResetPrevRequired, _innerResetPrevCount, inner.gap,
            inner.activeColor, inner.inactiveColor, innerGlow
        );

        const stockFlashElapsed = currentTime - _stockFlashStartTime;
        if (_stockFlashSegment >= 0 && stockFlashElapsed < STOCK_FLASH_DURATION) {
            const flashAlpha = 1 - stockFlashElapsed / STOCK_FLASH_DURATION;
            const segArc = getSegmentArc(_innerResetPrevRequired, inner.gap);
            const segStart = getSegmentStartAngle(_stockFlashSegment, _innerResetPrevRequired, inner.gap);
            drawFlashOverlay(ctx, cx, cy, inner.radius, inner.strokeWidth, segStart, segArc, flashAlpha, 12);
        }
    } else if (isMax && !isLevelUpReset) {
        const lastRequired = shipCfg.powerRequired[shipCfg.powerRequired.length - 1];
        drawSegmentedRing(
            ctx, cx, cy,
            inner.radius, inner.strokeWidth,
            lastRequired, lastRequired, inner.gap,
            outer.maxColor, inner.inactiveColor, innerGlow,
            maxPulseAlpha
        );
    } else if (!isLevelUpReset) {
        const required = shipCfg.powerRequired[powerLevel - 1] || 1;
        drawSegmentedRing(
            ctx, cx, cy,
            inner.radius, inner.strokeWidth,
            required, powerStock, inner.gap,
            inner.activeColor, inner.inactiveColor, innerGlow
        );

        // 通常時: アイテム取得フラッシュ
        const stockFlashElapsed = currentTime - _stockFlashStartTime;
        if (_stockFlashSegment >= 0 && stockFlashElapsed < STOCK_FLASH_DURATION) {
            const flashAlpha = 1 - stockFlashElapsed / STOCK_FLASH_DURATION;
            const segArc = getSegmentArc(required, inner.gap);
            const segStart = getSegmentStartAngle(_stockFlashSegment, required, inner.gap);
            drawFlashOverlay(ctx, cx, cy, inner.radius, inner.strokeWidth, segStart, segArc, flashAlpha, 12);
        }
    }

    // 中央レベル数字
    drawCenterLevel(ctx, cx, cy, powerLevel, isMax, centerGlow,
        isMax ? maxPulseAlpha : undefined);

    ctx.restore();
}

/**
 * 残量に応じたシールドカラーを計算
 * 100%: 緑 → 50%: 黄 → 0%: 赤
 */
function getShieldColor(percentage: number): [number, number, number] {
    if (percentage > 50) {
        // 緑(0,255,100) → 黄(255,255,0)
        const t = (100 - percentage) / 50;
        return [Math.round(255 * t), 255, Math.round(100 * (1 - t))];
    } else {
        // 黄(255,255,0) → 赤(255,50,80)
        const t = (50 - percentage) / 50;
        return [255, Math.round(255 - 205 * t), Math.round(80 * t)];
    }
}

/**
 * シールドアイコンを描画（シンプルな盾形状）
 */
function drawShieldIcon(
    p: p5,
    cx: number,
    cy: number,
    size: number,
    color: [number, number, number]
): void {
    p.push();
    p.translate(cx, cy);
    p.noStroke();
    p.fill(color[0], color[1], color[2], 200);

    // シンプルな盾形状（五角形ベース）
    p.beginShape();
    p.vertex(0, -size * 0.6);           // 上中央
    p.vertex(size * 0.5, -size * 0.4);  // 右上
    p.vertex(size * 0.4, size * 0.2);   // 右下
    p.vertex(0, size * 0.6);            // 下中央（尖り）
    p.vertex(-size * 0.4, size * 0.2);  // 左下
    p.vertex(-size * 0.5, -size * 0.4); // 左上
    p.endShape(p.CLOSE);
    p.pop();
}

/**
 * シールド円形インジケータ表示（左パネル下部）
 */
export function drawShieldIndicator(): void {
    const p = getP5();
    const ctx = p.drawingContext as CanvasRenderingContext2D;
    const leftCenterX = getLeftPanelCenterX();

    // SHIELDラベル（Share Tech Mono）
    p.textAlign(p.CENTER, p.TOP);
    p.textFont('Share Tech Mono');
    p.fill(255);
    p.textSize(11);
    p.text('SHIELD', leftCenterX, SHIELD_LABEL_Y);
    p.textFont('Orbitron');

    const shieldActive = getShieldActive();
    const cx = leftCenterX;
    const cy = SHIELD_CENTER_Y;
    const radius = 28;
    const currentTime = p.millis();

    // パーセンテージを事前計算（チャージ検出用）
    let percentage = 0;
    if (shieldActive) {
        const shieldTimer = getShieldTimer();
        const maxDuration = CONFIG.ships[getSelectedShip()].shieldDuration;
        percentage = (shieldTimer / maxDuration) * 100;
    }

    // シールド取得検出 → チャージアニメーション開始
    // ストアの shieldCharging フラグを直接参照し、applyItem() が設定したフラグを確実に検出
    if (getShieldCharging() && !_isCharging) {
        _chargeStartPercentage = _lastPercentage;
        _chargeStartTime = currentTime;
        _isCharging = true;
        setShieldCharging(true);
    }

    // シールド消失検出 → フェード開始
    if (_lastShieldActive && !shieldActive) {
        _fadeStartTime = currentTime;
    }
    _lastShieldActive = shieldActive;
    _lastPercentage = percentage;

    // フェード中かどうか判定
    const fadeElapsed = currentTime - _fadeStartTime;
    const isFading = !shieldActive && fadeElapsed < FADE_DURATION && _fadeStartTime > 0;
    const fadeAlpha = isFading ? 1 - (fadeElapsed / FADE_DURATION) : 0;

    if (shieldActive) {
        // アクティブ時: 円形インジケータ描画
        const color = getShieldColor(percentage);

        // フェード用に現在の状態を保存
        _fadeColor = color;
        _fadePercentage = percentage;

        // 残量警告点滅（20%以下で点滅開始）
        const WARNING_THRESHOLD = 20;
        const BLINK_SPEED = 300;  // ms周期
        let blinkAlpha = 1.0;
        let blinkGlow = 15;

        if (percentage <= WARNING_THRESHOLD) {
            // sin波で滑らかに点滅（0.6〜1.0の範囲、控えめ）
            const blink = Math.sin(p.millis() / BLINK_SPEED * Math.PI) * 0.5 + 0.5;
            blinkAlpha = 0.6 + blink * 0.4;
            blinkGlow = 12 + blink * 8;  // グロー強度も変動（控えめ）
        }

        // 背景リング（暗い色）
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(60, 60, 60, 0.8)';
        ctx.lineWidth = 6;
        ctx.stroke();

        const startAngle = -Math.PI / 2;  // 12時位置から開始

        if (_isCharging) {
            // チャージアニメーション中
            const chargeElapsed = currentTime - _chargeStartTime;
            const chargeProgress = Math.min(1, chargeElapsed / CHARGE_DURATION);
            if (chargeProgress >= 1) {
                _isCharging = false;
                setShieldCharging(false);
            }

            // レイヤー1: 薄い色で全量リングを表示
            ctx.shadowColor = `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.35)`;
            ctx.shadowBlur = 8;

            const endAngleFull = startAngle + (Math.PI * 2 * percentage / 100);
            ctx.beginPath();
            ctx.arc(cx, cy, radius, startAngle, endAngleFull);
            ctx.strokeStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.4)`;
            ctx.lineCap = 'round';
            ctx.stroke();

            // レイヤー2: 濃い色でチャージ進捗分だけ表示
            ctx.shadowColor = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${0.8 * blinkAlpha})`;
            ctx.shadowBlur = blinkGlow;

            const interpolated = _chargeStartPercentage + (percentage - _chargeStartPercentage) * chargeProgress;
            const chargeAngle = startAngle + (Math.PI * 2 * interpolated / 100);
            ctx.beginPath();
            ctx.arc(cx, cy, radius, startAngle, chargeAngle);
            ctx.strokeStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${blinkAlpha})`;
            ctx.lineCap = 'round';
            ctx.stroke();

            // レイヤー3: 先端グロー（チャージ完了前のみ）
            if (chargeProgress < 1) {
                const tipX = cx + radius * Math.cos(chargeAngle);
                const tipY = cy + radius * Math.sin(chargeAngle);

                ctx.shadowColor = `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.9)`;
                ctx.shadowBlur = 14;

                ctx.beginPath();
                ctx.arc(tipX, tipY, 4, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255, 255, 255, 0.9)`;
                ctx.fill();
            }
        } else {
            // 通常描画（チャージ完了後）
            ctx.shadowColor = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${0.8 * blinkAlpha})`;
            ctx.shadowBlur = blinkGlow;

            const endAngle = startAngle + (Math.PI * 2 * percentage / 100);
            ctx.beginPath();
            ctx.arc(cx, cy, radius, startAngle, endAngle);
            ctx.strokeStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${blinkAlpha})`;
            ctx.lineCap = 'round';
            ctx.stroke();
        }

        ctx.shadowBlur = 0;
        ctx.restore();

        // 中央シールドアイコン（点滅時はアルファ適用）
        const iconColor: [number, number, number] = [
            Math.round(color[0] * blinkAlpha + 255 * (1 - blinkAlpha) * 0.3),
            Math.round(color[1] * blinkAlpha + 255 * (1 - blinkAlpha) * 0.3),
            Math.round(color[2] * blinkAlpha + 255 * (1 - blinkAlpha) * 0.3)
        ];
        drawShieldIcon(p, cx, cy, 14, iconColor);

        // パーセンテージ表示（点滅時はアルファ適用）
        p.fill(color[0], color[1], color[2], 255 * blinkAlpha);
        p.textAlign(p.CENTER, p.TOP);
        p.textSize(12);
        p.text(Math.round(percentage) + '%', cx, cy + radius + 8);
    } else if (isFading) {
        // フェードアウト中: 最後の状態をフェードしながら表示
        const color = _fadeColor;

        // 背景リング（暗い色）
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(60, 60, 60, 0.8)';
        ctx.lineWidth = 6;
        ctx.stroke();

        // グロー効果 + 残量リング（フェード適用）
        ctx.shadowColor = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${0.8 * fadeAlpha})`;
        ctx.shadowBlur = 15 * fadeAlpha;

        const startAngle = -Math.PI / 2;
        const endAngle = startAngle + (Math.PI * 2 * _fadePercentage / 100);

        ctx.beginPath();
        ctx.arc(cx, cy, radius, startAngle, endAngle);
        ctx.strokeStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${fadeAlpha})`;
        ctx.lineCap = 'round';
        ctx.stroke();

        ctx.shadowBlur = 0;
        ctx.restore();

        // 中央シールドアイコン（フェード適用）
        const iconColor: [number, number, number] = [
            Math.round(color[0] * fadeAlpha + 80 * (1 - fadeAlpha)),
            Math.round(color[1] * fadeAlpha + 80 * (1 - fadeAlpha)),
            Math.round(color[2] * fadeAlpha + 80 * (1 - fadeAlpha))
        ];
        drawShieldIcon(p, cx, cy, 14, iconColor);

        // パーセンテージ表示（フェード適用）
        p.fill(color[0], color[1], color[2], 255 * fadeAlpha);
        p.textAlign(p.CENTER, p.TOP);
        p.textSize(12);
        p.text(Math.round(_fadePercentage) + '%', cx, cy + radius + 8);
    } else {
        // 非アクティブ時: 暗いリング
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(40, 40, 40, 0.6)';
        ctx.lineWidth = 6;
        ctx.stroke();
        ctx.restore();

        // 暗いアイコン
        drawShieldIcon(p, cx, cy, 14, [80, 80, 80]);

        // ---表示
        p.fill(80);
        p.textAlign(p.CENTER, p.TOP);
        p.textSize(10);
        p.text('---', cx, cy + radius + 8);
    }
}

/**
 * ウェーブ進行状況バー（左パネル、WAVE表示の下）
 */
export function drawWaveProgressBar(): void {
    const p = getP5();
    const waveEnemiesDefeated = getWaveEnemiesDefeated();
    const waveRequired = getEnemiesRequiredForWave();
    const waveProgress = waveEnemiesDefeated / waveRequired;
    const barX = 10;

    // 進行状況バー（テキストは敵カウンターが表示するため省略）
    p.fill(50);
    p.noStroke();
    p.rect(barX, WAVE_PROGRESS_Y, BAR_WIDTH, BAR_HEIGHT, 3);
    p.fill(255, 0, 255);
    p.rect(barX, WAVE_PROGRESS_Y, BAR_WIDTH * waveProgress, BAR_HEIGHT, 3);
}
